import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  fiscalFxSourceReviewInputSchema,
  type FiscalFxSourceReviewInput,
  type FiscalFxSourceReviewPreview,
  type FiscalFxRate,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { usePermissions } from '@/features/auth';
import {
  useFiscalSourceCandidates,
  useFiscalSourcePreview,
  useCreateFiscalSourceReview,
} from '../api/use-fiscal-source-review';
import { FinancialFormProtection } from './financial-form-protection';
import { formatLedgerMoney } from '../lib/ledger-format';

type Candidate = NonNullable<ReturnType<typeof useFiscalSourceCandidates>['data']>['data'][number];

function RecipientSourcePreview({
  review,
}: {
  review: NonNullable<FiscalFxSourceReviewPreview['recipientSelfAssessment']>;
}) {
  const { t, i18n } = useTranslation();
  const money = (value: string) => formatLedgerMoney(value, 'EUR', i18n.language);
  return (
    <div className="space-y-4 break-words">
      <h3 className="font-semibold">{t('fiscalFx.recipientReviewTitle')}</h3>
      <p>
        {t('ledger.selfAssessment.case')}:{' '}
        {t(`ledger.selfAssessment.cases.${review.original.review.case}`)}
        <br />
        {review.original.legalBasis}
      </p>
      <p className="whitespace-pre-wrap">
        {t('fiscalFx.reviewEvidence')}: {review.original.review.reason}
      </p>
      {review.positions.map((row) => (
        <div key={row.position} className="space-y-3 border-t pt-3">
          <p className="font-medium">
            {row.position} ·{' '}
            {formatLedgerMoney(
              row.sourceOriginalNet,
              review.original.sourceCurrency ?? 'EUR',
              i18n.language,
            )}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {(['original', 'corrected'] as const).map((version) => {
              const title = t(
                version === 'original'
                  ? 'fiscalFx.recipientOriginal'
                  : 'fiscalFx.recipientCorrected',
              );
              return (
                <div key={version} role="group" aria-label={title} className="space-y-2">
                  <h4 className="font-medium">{title}</h4>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-muted-foreground">
                        {t('fiscalFx.recipientFiscalNet')}
                      </dt>
                      <dd>
                        {money(
                          version === 'original' ? row.originalFiscalNet : row.correctedFiscalNet,
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">
                        {t('ledger.selfAssessment.legal')}
                      </dt>
                      <dd>
                        {money(
                          version === 'original' ? row.originalLegalTax : row.correctedLegalTax,
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">
                        {t('fiscalFx.reviewDeductible')}
                      </dt>
                      <dd>
                        {money(
                          version === 'original'
                            ? row.originalDeductibleTax
                            : row.correctedDeductibleTax,
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>
              );
            })}
          </div>
          <dl className="grid gap-3 border-t pt-3 sm:grid-cols-3">
            {(
              [
                ['recipientOutputChange', row.outputCorrection],
                ['recipientInputChange', row.inputCorrection],
                ['recipientCostChange', row.costCorrection],
              ] as const
            ).map(([label, value]) => (
              <div key={label}>
                <dt className="text-sm text-muted-foreground">{t(`fiscalFx.${label}`)}</dt>
                <dd className="font-medium">
                  {value.startsWith('-') ? '' : '+'}
                  {money(value)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
      <p className="text-sm text-muted-foreground">{t('fiscalFx.recipientFixedHint')}</p>
    </div>
  );
}
function ReviewEditor({
  candidate,
  rates,
  onClose,
}: {
  candidate: Candidate;
  rates: FiscalFxRate[];
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const previewCommand = useFiscalSourcePreview();
  const save = useCreateFiscalSourceReview();
  const [preview, setPreview] = useState<FiscalFxSourceReviewPreview | null>(null);
  const [commandKey, setCommandKey] = useState(() => crypto.randomUUID());
  const [error, setError] = useState('');
  const form = useForm<FiscalFxSourceReviewInput>({
    resolver: zodResolver(fiscalFxSourceReviewInputSchema),
    defaultValues: {
      invoiceId: candidate.invoiceId,
      settlementId: candidate.settlementId,
      reason: '',
      fiscalRateId: rates.length === 1 ? (rates[0]?.id ?? '') : '',
    },
  });
  const signature = JSON.stringify(form.watch());
  useEffect(() => {
    setPreview(null);
  }, [signature]);
  const pending = previewCommand.isPending || save.isPending;
  const calculate = form.handleSubmit(async (input) => {
    if (pending || !can('ledger:configure')) return;
    setError('');
    try {
      setPreview(await previewCommand.mutateAsync(input));
      setCommandKey(crypto.randomUUID());
    } catch (failure) {
      setError(
        failure instanceof Error && failure.message ? failure.message : 'fiscalFx.reviewError',
      );
    }
  });
  const approve = form.handleSubmit(async (input) => {
    if (!preview || pending || !can('ledger:configure') || !can('ledger:post')) return;
    setError('');
    try {
      await save.mutateAsync({
        ...input,
        sourceHash: preview.sourceHash,
        idempotencyKey: commandKey,
      });
      toast.success(t('fiscalFx.reviewSaved'));
      onClose();
    } catch (failure) {
      setError(
        failure instanceof Error && failure.message ? failure.message : 'fiscalFx.reviewError',
      );
    }
  });
  return (
    <FinancialFormProtection dirty={form.formState.isDirty} pending={pending} onClose={onClose}>
      {(close) => (
        <Sheet
          open
          onOpenChange={(open) => {
            if (!open) close();
          }}
        >
          <SheetContent
            closeLabel={t('common.close')}
            className="overflow-y-auto sm:max-w-xl [&_button]:min-h-11 [&_select]:min-h-11"
          >
            <SheetTitle>
              {t('fiscalFx.reviewAction')} · {candidate.number}
            </SheetTitle>
            <SheetDescription>{t('fiscalFx.reviewHint')}</SheetDescription>
            <form
              className="mt-5 space-y-4"
              onSubmit={preview ? approve : calculate}
              onKeyDown={(event) => {
                if ((event.ctrlKey || event.metaKey) && ['s', 'Enter'].includes(event.key)) {
                  event.preventDefault();
                  if (!pending) void (preview ? approve() : calculate());
                }
              }}
            >
              <p>
                {t('fiscalFx.reviewRateDate')}: {candidate.rateTaxDate}
                <br />
                {t('fiscalFx.reviewReportingDate')}: {candidate.reportingTaxDate}
              </p>
              <div className="space-y-1">
                <Label htmlFor="review-fiscal-rate">{t('fiscalFx.reviewRate')}</Label>
                <select
                  id="review-fiscal-rate"
                  className="w-full rounded-md border bg-background p-2"
                  {...form.register('fiscalRateId')}
                  disabled={pending || !can('ledger:configure')}
                  aria-invalid={!!form.formState.errors.fiscalRateId}
                >
                  <option value="">{t('fiscalFx.reviewRate')}</option>
                  {rates.map((rate) => (
                    <option key={rate.id} value={rate.id}>
                      {rate.sourceCurrency} · {rate.quote} · {t(`fiscalFx.${rate.quoteDirection}`)}{' '}
                      · {rate.validFrom} · {t('fiscalFx.version')} {rate.version}
                    </option>
                  ))}
                </select>
                {form.formState.errors.fiscalRateId ? (
                  <p className="text-destructive">{t('fiscalFx.errors.missing')}</p>
                ) : null}
              </div>
              <div className="space-y-1">
                <Label htmlFor="review-fiscal-reason">{t('fiscalFx.reviewReason')}</Label>
                <Textarea
                  id="review-fiscal-reason"
                  {...form.register('reason')}
                  disabled={pending || !can('ledger:configure')}
                  rows={4}
                  aria-invalid={!!form.formState.errors.reason}
                />
                {form.formState.errors.reason ? (
                  <p className="text-destructive">{t('fiscalFx.reviewError')}</p>
                ) : null}
              </div>
              {error ? (
                <p role="alert" className="text-destructive">
                  {t(error, { defaultValue: t('fiscalFx.reviewError') })}
                </p>
              ) : null}
              <Button
                type="button"
                variant="outline"
                disabled={pending || !can('ledger:configure')}
                onClick={() => void calculate()}
              >
                {t('fiscalFx.reviewPreview')}
              </Button>
              {preview ? (
                <section
                  aria-label={t('fiscalFx.reviewPreview')}
                  className="space-y-3 rounded border p-3"
                >
                  {preview.recipientSelfAssessment ? (
                    <RecipientSourcePreview review={preview.recipientSelfAssessment} />
                  ) : null}
                  {preview.vendorFiscalFx ? (
                    <div className="space-y-1">
                      <p>
                        {t('fiscalFx.reviewEvidence')}: {preview.vendorFiscalFx.review.evidenceText}
                      </p>
                      {preview.vendorFiscalFx.rows.map((row) => (
                        <p key={row.position}>
                          {row.position} · {row.taxRate}% · {t('fiscalFx.vendorLegal')}:{' '}
                          {formatLedgerMoney(row.legalTax, 'EUR', i18n.language)} ·{' '}
                          {t('fiscalFx.reviewDeductible')}:{' '}
                          {formatLedgerMoney(row.deductibleTax, 'EUR', i18n.language)}
                        </p>
                      ))}
                    </div>
                  ) : null}
                  {!preview.recipientSelfAssessment
                    ? preview.comparison.map((row) => (
                        <dl key={row.index} className="grid gap-2 sm:grid-cols-3">
                          <div>
                            <dt>
                              {row.taxRatePct}% · {t('fiscalFx.reviewPosted')}
                            </dt>
                            <dd>{formatLedgerMoney(row.postedTax, 'EUR', i18n.language)}</dd>
                          </div>
                          <div>
                            <dt>{t('fiscalFx.reviewFiscal')}</dt>
                            <dd>{formatLedgerMoney(row.fiscalTax, 'EUR', i18n.language)}</dd>
                          </div>
                          <div>
                            <dt>{t('fiscalFx.reviewDifference')}</dt>
                            <dd>{formatLedgerMoney(row.correction, 'EUR', i18n.language)}</dd>
                          </div>
                        </dl>
                      ))
                    : null}
                  <Button
                    type="submit"
                    disabled={pending || !can('ledger:configure') || !can('ledger:post')}
                  >
                    {t('fiscalFx.reviewApprove')}
                  </Button>
                </section>
              ) : null}
            </form>
          </SheetContent>
        </Sheet>
      )}
    </FinancialFormProtection>
  );
}
export function FiscalSourceReviews({ rates }: { rates: FiscalFxRate[] }) {
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Candidate | null>(null);
  const query = useFiscalSourceCandidates(page, q);
  const replaced = new Set(rates.flatMap((rate) => (rate.supersedesId ? [rate.supersedesId] : [])));
  return (
    <section className="space-y-3" aria-label={t('fiscalFx.reviewTitle')}>
      <h2 className="text-lg font-semibold">{t('fiscalFx.reviewTitle')}</h2>
      <p className="text-sm text-muted-foreground">{t('fiscalFx.reviewHint')}</p>
      <Label htmlFor="fiscal-source-search">{t('fiscalFx.reviewSearch')}</Label>
      <Input
        id="fiscal-source-search"
        value={q}
        onChange={(event) => {
          setQ(event.target.value);
          setPage(1);
        }}
      />
      {query.isLoading ? <Skeleton className="h-24" aria-label={t('fiscalFx.loading')} /> : null}
      {query.isError ? (
        <div role="alert">
          <p>{t('fiscalFx.reviewError')}</p>
          <Button onClick={() => void query.refetch()}>{t('fiscalFx.retry')}</Button>
        </div>
      ) : null}
      {query.data?.data.length === 0 ? <p>{t('fiscalFx.reviewEmpty')}</p> : null}
      <div className="grid gap-3 md:grid-cols-2">
        {query.data?.data.map((candidate) => (
          <article
            className="space-y-2 rounded border p-3"
            key={`${candidate.invoiceId}:${candidate.settlementId ?? ''}`}
          >
            <h3 className="font-medium">
              {candidate.number} · {candidate.currency}
            </h3>
            <p>
              {t(
                candidate.sourceKind === 'VENDOR_INVOICE'
                  ? 'fiscalFx.reviewVendor'
                  : candidate.sourceKind === 'CUSTOMER_CASH_RECEIPT'
                    ? 'fiscalFx.reviewReceipt'
                    : candidate.sourceKind === 'CUSTOMER_CASH_REVERSAL'
                      ? 'fiscalFx.reviewReversal'
                      : candidate.sourceKind === 'CUSTOMER_DISCOUNT'
                        ? 'fiscalFx.reviewDiscount'
                        : 'fiscalFx.reviewSupply',
              )}{' '}
              · {candidate.reportingTaxDate}
            </p>
            {candidate.sourceKind === 'VENDOR_INVOICE' ? (
              <div className="space-y-1 text-sm">
                <p>
                  {t('fiscalFx.reviewSupplier')}: {candidate.supplierName}
                </p>
                <p>
                  {t('fiscalFx.reviewRateDate')}: {candidate.rateTaxDate}
                </p>
                <p>
                  {t('fiscalFx.reviewPosted')}:{' '}
                  {formatLedgerMoney(candidate.postedTax, 'EUR', i18n.language)}
                </p>
                {candidate.legalTax !== undefined ? (
                  <p>
                    {t('fiscalFx.vendorLegal')}:{' '}
                    {formatLedgerMoney(candidate.legalTax, 'EUR', i18n.language)}
                  </p>
                ) : null}
                {candidate.deductibleTax !== undefined ? (
                  <p>
                    {t('fiscalFx.reviewDeductible')}:{' '}
                    {formatLedgerMoney(candidate.deductibleTax, 'EUR', i18n.language)}
                  </p>
                ) : null}
                <p>
                  {t('fiscalFx.reviewEvidence')}: {candidate.sourceEvidence}
                </p>
              </div>
            ) : null}
            {candidate.blockedReason ? <p role="status">{t(candidate.blockedReason)}</p> : null}
            {can('ledger:configure') ? (
              <Button
                variant="outline"
                disabled={!candidate.reviewable}
                onClick={() => setSelected(candidate)}
              >
                {t('fiscalFx.reviewAction')}
              </Button>
            ) : null}
          </article>
        ))}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}>
          {t('fiscalFx.reviewPrevious')}
        </Button>
        <Button
          variant="outline"
          disabled={!query.data || page >= query.data.meta.totalPages}
          onClick={() => setPage(page + 1)}
        >
          {t('fiscalFx.reviewNext')}
        </Button>
      </div>
      {selected ? (
        <ReviewEditor
          key={`${selected.invoiceId}:${selected.settlementId ?? ''}`}
          candidate={selected}
          rates={rates.filter(
            (rate) =>
              (selected.pinnedFiscalRateId
                ? rate.id === selected.pinnedFiscalRateId
                : !replaced.has(rate.id)) &&
              rate.sourceCurrency === selected.currency &&
              rate.validFrom <= selected.rateTaxDate &&
              rate.validTo >= selected.rateTaxDate,
          )}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </section>
  );
}
